import {
  PRESETS,
  remainingAt,
  statusAt,
  type PresetId,
  type Status,
  type TimerState,
} from './state.js';

export type TimerColor = 'white' | 'gold' | 'red';

/** Seconds shown on screen: a countdown displays 12:00 for the whole first second. */
export function displaySecondsFromMs(remainingMs: number): number {
  return Math.max(0, Math.ceil(remainingMs / 1000));
}

/** MM:SS, minutes may exceed 59 but never will for our presets. */
export function formatMMSS(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

/**
 * Colour thresholds are inclusive:
 *   03:01 -> white, 03:00 -> gold, 01:01 -> gold, 01:00 -> red, 00:00 -> red
 */
export function colorForSeconds(displaySeconds: number): TimerColor {
  if (displaySeconds <= 60) return 'red';
  if (displaySeconds <= 180) return 'gold';
  return 'white';
}

export interface TimerSnapshot {
  status: Status;
  presetId: PresetId;
  presetLabel: string;
  remainingMs: number;
  displaySeconds: number;
  text: string;
  color: TimerColor;
  version: number;
}

/** Everything the UI needs, derived from state + clock. Pure. */
export function snapshotAt(state: TimerState, now: number): TimerSnapshot {
  const remainingMs = remainingAt(state, now);
  const displaySeconds = displaySecondsFromMs(remainingMs);
  return {
    status: statusAt(state, now),
    presetId: state.presetId,
    presetLabel: PRESETS[state.presetId].label,
    remainingMs,
    displaySeconds,
    text: formatMMSS(displaySeconds),
    color: colorForSeconds(displaySeconds),
    version: state.version,
  };
}

export const STATUS_LABELS: Record<Status, string> = {
  idle: 'BEREIT',
  running: 'LÄUFT',
  paused: 'PAUSE',
  finished: 'ABGELAUFEN',
};
